import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '../services/_apiBase';
import {
    fetchShellyConfig, saveShellyConfig,
    fetchShellyDevices, triggerShellyPollNow,
    ShellyDevice, ShellyCloudConfig,
} from '../services/api';
import { colors, ui } from '../styles/shared';
import ErrorBanner from './shared/ErrorBanner';
import PageHeader from './shared/PageHeader';
import DevicesTab from './shelly/DevicesTab';
import LiveDataTab from './shelly/LiveDataTab';

// ── Inställningar för Shelly Cloud ────────────────────────────────────────────

const CloudConfigPanel: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
    const [cfg, setCfg] = useState<ShellyCloudConfig | null>(null);
    const [serverUri, setServerUri] = useState('');
    const [authKey, setAuthKey] = useState('');
    const [pollInterval, setPollInterval] = useState(60);
    const [enabled, setEnabled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [polling, setPolling] = useState(false);
    const [pollResult, setPollResult] = useState<{ ok: boolean; msg: string } | null>(null);

    useEffect(() => {
        fetchShellyConfig().then(c => {
            setCfg(c);
            setServerUri(c.server_uri ?? '');
            setPollInterval(c.poll_interval_seconds);
            setEnabled(c.enabled);
        });
    }, []);

    const handleSave = async () => {
        setSaving(true); setMsg(null);
        try {
            const payload: any = { server_uri: serverUri, poll_interval_seconds: pollInterval, enabled };
            // Nyckeln skickas bara om användaren skrivit något; tomt fält = behåll den som finns
            if (authKey.trim()) payload.auth_key = authKey.trim();
            await saveShellyConfig(payload);
            setAuthKey('');
            setCfg(await fetchShellyConfig());
            setMsg('Sparat');
            onSaved();
        } catch (e: any) {
            setMsg(getErrorMessage(e, 'Kunde inte spara'));
        } finally {
            setSaving(false);
        }
    };

    const handlePollNow = async () => {
        setPolling(true); setPollResult(null);
        try {
            const r = await triggerShellyPollNow();
            setPollResult({ ok: true, msg: r.message });
            setCfg(await fetchShellyConfig());
        } catch (e: any) {
            setPollResult({ ok: false, msg: getErrorMessage(e, 'Hämtningen misslyckades') });
        } finally {
            setPolling(false);
        }
    };

    return (
        <div style={{ ...ui.card, border: '1px solid #dbeafe', background: '#f0f9ff', marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>
                ☁️ Anslutning till Shelly Cloud
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                    <label style={ui.label}>Serveradress</label>
                    <input
                        value={serverUri}
                        onChange={e => setServerUri(e.target.value)}
                        placeholder="t.ex. https://shelly-12-eu.shelly.cloud"
                        style={ui.input}
                    />
                    <div style={ui.hint}>
                        Finns i Shelly Cloud-appen → User settings → Authorization cloud key
                    </div>
                </div>
                <div>
                    <label style={ui.label}>
                        Nyckel {cfg?.auth_key_set && <span style={{ color: '#10b981', fontWeight: 400 }}>({cfg.auth_key} — sparad)</span>}
                    </label>
                    <input
                        type="password"
                        value={authKey}
                        onChange={e => setAuthKey(e.target.value)}
                        placeholder={cfg?.auth_key_set ? 'Lämna tomt för att behålla nuvarande nyckel' : 'Klistra in din nyckel'}
                        style={ui.input}
                        autoComplete="new-password"
                    />
                </div>
                <div>
                    <label style={ui.label}>Hämta var (sekunder, minst 30)</label>
                    <input
                        type="number"
                        value={pollInterval}
                        min={30}
                        onChange={e => setPollInterval(Number(e.target.value))}
                        style={{ ...ui.input, width: 100 }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={e => setEnabled(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                        />
                        Hämta värden från molnet
                    </label>
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button style={{ ...ui.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                    {saving ? 'Sparar…' : 'Spara och använd'}
                </button>
                <button style={{ ...ui.btn(colors.info), opacity: polling ? 0.6 : 1 }} disabled={polling} onClick={handlePollNow}>
                    {polling ? 'Hämtar…' : '⚡ Hämta nu'}
                </button>
                {msg && (
                    <span style={{ fontSize: 13, color: msg === 'Sparat' ? colors.success : colors.danger }}>
                        {msg}
                    </span>
                )}
                {pollResult && (
                    <span style={{ fontSize: 13, color: pollResult.ok ? colors.success : colors.danger }}>
                        {pollResult.msg}
                    </span>
                )}
                {cfg?.last_poll_at && (
                    <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>
                        Senaste hämtning: {new Date(cfg.last_poll_at).toLocaleString('sv-SE')}
                        {cfg.last_error && <span style={{ color: colors.danger }}> — {cfg.last_error}</span>}
                    </span>
                )}
            </div>
        </div>
    );
};

// ── Sidan ─────────────────────────────────────────────────────────────────────

type ShellyTab = 'devices' | 'live';

const TABS: { key: ShellyTab; label: string }[] = [
    { key: 'devices', label: 'Enheter' },
    { key: 'live', label: 'Aktuella värden' },
];

const Shelly: React.FC = () => {
    const [tab, setTab] = useState<ShellyTab>('devices');
    const [devices, setDevices] = useState<ShellyDevice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Enhetslistan behövs i båda flikarna, så den ligger här
    const load = async () => {
        setLoading(true); setError(null);
        try { setDevices(await fetchShellyDevices()); }
        catch (e: any) { setError(e.message || 'Kunde inte hämta enheterna'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    return (
        <div style={{ padding: 28, maxWidth: 1000, margin: '0 auto' }}>

            <PageHeader icon="⚡" title="Shelly" description="Värden från Shelly-elmätare, hämtade via Shelly Cloud." />

            <div className="surveillance-tabs">
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {tab === 'devices' && (
                <>
                    <CloudConfigPanel onSaved={load} />
                    <DevicesTab devices={devices} loading={loading} onReload={load} onError={setError} />
                </>
            )}

            {tab === 'live' && (
                <LiveDataTab devices={devices} onError={setError} onGoToDevices={() => setTab('devices')} />
            )}

        </div>
    );
};

export default Shelly;
